import { Box, Typography } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { ANALYSIS_FAMILIES } from '@/features/candleCard/reportAdapters';

/**
 * What the card has collected so far.
 *
 * This is the reason to come back, and it is deliberately NOT a score about
 * the person's face. It counts which of the four analyses they have had —
 * something they did, not something they are. A slot lights when a report of
 * that family exists; an empty one says plainly what it would take to fill it.
 *
 * The same logic rules out a composite "Candle score": blending four bots'
 * incomparable metrics into one number people would watch climb would mean
 * inventing a figure with no clinical meaning and attaching it to someone's
 * appearance.
 */
export default function CoverageRow({ haveFamilies, t, lang, onPick }) {
  const filled = ANALYSIS_FAMILIES.filter((f) => haveFamilies.has(f.key)).length;
  // Persian numerals in a Persian page; the count sits in a sentence, so a
  // Latin digit beside Persian text is the one thing that reads as unfinished.
  const fmt = (n) => new Intl.NumberFormat(lang === 'fa' ? 'fa-IR' : 'en-GB').format(n);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        gap: 1, mb: 1.25 }}>
        <Typography variant="subtitle1" fontWeight={700} component="h2">
          {t('candleCard.coverage.title')}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('candleCard.coverage.count', { filled: fmt(filled), total: fmt(ANALYSIS_FAMILIES.length) })}
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gap: 1,
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' } }}>
        {ANALYSIS_FAMILIES.map((f) => {
          const have = haveFamilies.has(f.key);
          return (
            <Box
              key={f.key}
              component={onPick && !have ? 'button' : 'div'}
              onClick={onPick && !have ? () => onPick(f.key) : undefined}
              type={onPick && !have ? 'button' : undefined}
              sx={{
                textAlign: 'start',
                font: 'inherit',
                cursor: onPick && !have ? 'pointer' : 'default',
                p: 1.5,
                borderRadius: 3,
                border: '1px solid',
                borderColor: have ? 'rgba(192,167,134,.75)' : 'divider',
                background: have
                  ? 'linear-gradient(150deg,rgba(192,167,134,.22),rgba(192,167,134,.06))'
                  : '#FFFFFF',
                display: 'flex',
                flexDirection: 'column',
                gap: .75,
                minHeight: 86,
              }}
            >
              <Box sx={{
                width: 22, height: 22, borderRadius: '50%',
                display: 'grid', placeItems: 'center',
                bgcolor: have ? '#C0A786' : 'transparent',
                border: have ? 'none' : '1px dashed',
                borderColor: 'divider',
                color: '#11223E',
              }}>
                {have ? <CheckRoundedIcon sx={{ fontSize: 15 }} /> : null}
              </Box>
              <Typography variant="body2" sx={{
                fontWeight: have ? 700 : 500,
                color: have ? 'text.primary' : 'text.secondary',
                lineHeight: 1.3,
              }}>
                {t(f.labelKey)}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
